import type { StaticEntityType } from "@config";
import { useViewContext } from "@editor-providers";
import { LinkPopUp } from "@library/components/LinkPopUp";
import { type MouseEvent, useMemo, useRef, useState } from "react";
import { isStatic } from "@/shared";
import {
  VARIABLE_PILL_ATTR,
  VARIABLE_PILL_CLASS,
} from "@/shared/utils/variable-pill";
import { useTextAnimation } from "../hooks";
import { showFinalState } from "../utils/final-state";
import { emptyBlockAlign } from "../utils/placeholder";
import { RichTextElement, StyledRichTextWrapper } from "../styles/Text.styled";
import "../styles/index.scss";
import type { EditorDataProps } from "../types";

interface Props {
  id: string;
  width: number;
  horizontalAlignment?: string;
  content: string;
  animation?: null | {
    speed: string;
    baseOpacity: string;
  };
  placeholder?: string;
}

export const TextLibrary = ({
  entity,
  editorData,
}: {
  entity: StaticEntityType;
  editorData?: EditorDataProps;
}) => {
  const { view } = useViewContext();
  const { settings, id } = entity as StaticEntityType<Props>;
  const [showLinkPopUpSrc, setShowLinkPopUpSrc] = useState<null | string>(null);
  const ref = useRef<HTMLDivElement | null>(null);
  const isPdf = view === "pdf";
  const staticView = isStatic();

  const storedContent = editorData?.content || settings.content || "";
  // Counters and highlights animate for readers and in the editor, so the
  // author sees the speed they picked; the PDF shows them finished.
  const content = useMemo(
    () => (isPdf ? showFinalState(storedContent) : storedContent),
    [isPdf, storedContent],
  );
  const width = editorData?.width || settings.width;
  useTextAnimation({ ref, disabled: isPdf });

  const handleClick = (e: MouseEvent) => {
    if (isPdf) return;

    const target = e.target as HTMLElement;

    // Variable pill click — route to the editor's popover-open callback
    // when one is supplied (version editor). This runs regardless of the
    // `editorData` early-return below so the inactive text element doesn't
    // need to be activated first.
    const pill = target.closest<HTMLElement>(`.${VARIABLE_PILL_CLASS}`);
    if (pill && editorData?.onPillClick) {
      const name = pill.getAttribute(VARIABLE_PILL_ATTR);
      if (name) {
        // The callback returns `true` when it handled the click (e.g. opened a
        // popover); only then do we swallow it so the element stays inactive.
        // Otherwise the click bubbles and the element activates as usual.
        const handled = editorData.onPillClick(pill, name);
        if (handled) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
      }
    }

    if (editorData) return;

    const link = target.tagName === "A" ? target : target.closest("a");
    if (link && link.getAttribute("data-open-in-popup") === "true") {
      e.preventDefault();
      const href = link.getAttribute("href");
      if (href) setShowLinkPopUpSrc(href);
    }
  };

  const parsePdfLink = (content: string) => {
    const tmpTree = new DOMParser().parseFromString(content, "text/html");
    const links = tmpTree.querySelectorAll("a[data-anchor-trigger]");
    links.forEach((link) => {
      const anchorTrigger = link.getAttribute("data-anchor-trigger");
      if (anchorTrigger) {
        link.setAttribute(
          "href",
          window.location.href.split("?")[0] +
            "#" +
            encodeURIComponent(anchorTrigger),
        );
        link.setAttribute("target", "_blank");
      }
    });
    return tmpTree.body.innerHTML;
  };

  const { isContentEmpty, placeholderAlign } = useMemo(() => {
    if (!settings.placeholder)
      return { isContentEmpty: content.length === 0, placeholderAlign: null };
    const tmpTree = new DOMParser().parseFromString(content, "text/html");
    const isEmpty = !(tmpTree.body.textContent?.trim() || "");
    return {
      isContentEmpty: isEmpty,
      placeholderAlign: isEmpty ? emptyBlockAlign(tmpTree) : null,
    };
  }, [content, settings.placeholder]);

  return (
    <StyledRichTextWrapper
      ref={ref}
      $horizontalAlignment={settings?.horizontalAlignment}
      $animation={isPdf || staticView ? null : settings?.animation}
      data-rich-text-has-animation={
        !isPdf && !staticView && settings?.animation != null
      }
      className="rich-text-wrapper"
      {...EDITOR_ATTRIBUTES({ isEditor: view === "editor" })}
    >
      {isContentEmpty && (view === "editor" || view === "thumbnail") ? (
        <div
          className={
            editorData?.inheritPlaceholderTypography
              ? "fr-placeholder --sd-typography content"
              : "fr-placeholder"
          }
          data-align={
            editorData?.inheritPlaceholderTypography
              ? (placeholderAlign ?? undefined)
              : undefined
          }
          style={{ display: "flex", width: (width || 100) + "%" }}
          dangerouslySetInnerHTML={{
            __html: settings.placeholder || "Click to add text",
          }}
        ></div>
      ) : (
        <RichTextElement
          id={`rich-text-${id}`}
          className={
            "content rich-text-element" +
            (editorData?.isAdmin ? " sd-admin" : "")
          }
          width={width}
          dangerouslySetInnerHTML={{
            __html: isPdf ? parsePdfLink(content) : content,
          }}
          onClick={handleClick}
        />
      )}

      {showLinkPopUpSrc && (
        <LinkPopUp
          src={showLinkPopUpSrc}
          closePopUp={() => setShowLinkPopUpSrc(null)}
        />
      )}
    </StyledRichTextWrapper>
  );
};

const EDITOR_ATTRIBUTES = ({ isEditor }: { isEditor: boolean }) => {
  const payload: Record<string, string> = {
    "data-dnd-trigger": "true",
  };
  return isEditor ? payload : {};
};
